// context/fileContext.js

import { createContext, useState } from "react";
import useAuth from "../stores/authStore";
import secureLocalStorage from "react-secure-storage";
import { api } from "../utils/axios-instance";
import { useDepartment } from "../stores/departmentStore";

const fileContext = createContext();

export const FileState = (props) => {
  const [files, setFiles] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [departmentFiles, setDepartmentFiles] = useState([]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [openInChat, setOpenInChat] = useState(false);
  const [allFiles, setAllFiles] = useState([]);

  // currFile used to be a single shared value, which silently corrupted
  // state whenever two FileView panes were mounted at once (dual-view /
  // OpenFiles): whichever editor finished loading last clobbered the
  // other pane's file, so the second pane's saveVersion() and re-opens
  // would operate on the wrong file. Keyed by fileId so each pane has
  // its own independent slot. setCurrFile/currFile below are kept as
  // backward-compatible aliases for any single-file callers.
  const [currFileMap, setCurrFileMap] = useState({});
  const setCurrFileFor = (fileId, file) => {
    if (!fileId) return;
    setCurrFileMap((prev) => ({ ...prev, [fileId]: file }));
  };
  const getCurrFileFor = (fileId) => currFileMap[fileId] ?? null;

  // Legacy single-value API, retained so existing single-file-view call
  // sites keep working without changes. New/dual-view code should prefer
  // setCurrFileFor/getCurrFileFor.
  const [currFile, setCurrFile] = useState(null);
  const departments = useDepartment((state) => state.departments);
  const location = window.location.pathname;

  // use this to refresh recycle bin
  const [refreshRecycleBin, setRefreshRecycleBin] = useState(false);

  // use this to refresh folders list
  const [refreshFolders, setRefreshFolders] = useState(false);

  const formatFileSize = useAuth((state) => state.formatFileSize);

  const removeFile = async (fileid) => {
    let removedFile = filteredData.filter((file) => file.id !== fileid);
    setAnchorEl(null);
    setFilteredData(removedFile);
  };

  const updateFilesState = async (value = 0) => {
    try {
      let url;

      // Determine the URL based on the selected tab
      switch (parseInt(value)) {
        case 0:
          url = `/file/files`;
          break;
        case 1:
          url = `/file/files?type=shared`;
          break;
        case 2:
          url = `/file/files?type=received`;
          break;
        case 3:
          url = `/file/files?type=owned`;
          break;
        case 4:
          url = `/file/files?type=pinned`;
          break;
        default:
          url = `/file/files`;
          break;
      }

      if (url) {
        const getFiles = await api.get(url);

        setFiles(getFiles.data);

        const cacheKey = "recentFilesCache";

        const mappedFiles = getFiles.data.map((file) => {
          // destucture and extract dept name of every file
          try {
            const [{ depts }, ...extra] = file.file_info;
            const [{ name }, ...more] = depts;
            file.department = name;
          } catch (err) {
            file.department = "";
          }

          const filteredDepartment = departments.filter(
            (dept) => dept.id === file.department_ids[0]
          );
          return {
            id: file.id,
            folderId: file.folder_id ?? "",
            versionId: file.versionId ?? "",
            name: file?.name?.substring(0, 80),
            profilePic: file.profilePic,
            size: formatFileSize(file.metadata.size),
            dept: file.department_ids,
            owner: file.owner_id,
            mimetype: file.metadata.mimetype,
            status: "Team",
            security: "Enhanced",
            color: filteredDepartment[0]?.metadata.bg,
            downloadUrl: file.download_url,
            isLocked: file?.is_locked,
            hasPassword: file?.has_password,
            password: file?.password,
            isPinned: file?.is_pinned,
            drive_file_id: file?.drive_file_id,
            drive_link: file?.drive_link,
            lastUpdate: new Date(file.metadata.lastModified).toLocaleString(
              "en-IN",
              {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              }
            ),
            versions: file.versions ?? [],
            currVersion: file.curr_version ?? file.id,
          };
        });

        // Sort the mappedFiles array based on the lastUpdate property
        mappedFiles.sort((a, b) => {
          return new Date(b.lastUpdate) - new Date(a.lastUpdate);
        });
        const filteredFiles = mappedFiles.filter((file) => {
          try {
            const profileData = JSON.parse(
              secureLocalStorage.getItem("profileData")
            );
            // if (
            //   value === 0 &&
            //   profileData.role_priv !== "org_admin" &&
            //   file.owner !== profileData.id
            // ) {
            //   return null;
            // }
            // Note: pinned files (may be do not show pinned files from all departments)
            // if (
            //   value === 4 &&
            //   file.owner !== profileData.id &&
            //   profileData.role_priv !== "org_admin"
            // ) {
            //   return null;
            // }
            return file;
          } catch (err) {
            console.log(err);
            return null;
          }
        });

        // filter out files which have a folder id
        // V2: folder-contained files are still members of whichever
        // department/share scope they belong to — don't hide them from
        // the general files list, only the dedicated folder-browser view
        // should restrict by folder_id.
        const nonFolderFiles = filteredFiles;
        console.log({ filteredFiles });

        // Replace the cached recent files data with the new data
        secureLocalStorage.setItem(cacheKey, JSON.stringify(nonFolderFiles));

        // Update the state with the new data
        // setFilteredData(mappedFiles);

        if (location !== "/dashboard") {
          // If location is other than "dashboard", send only the first 5 items
          setFilteredData(nonFolderFiles);
        } else {
          // If location is "dashboard", send all filtered data
          setFilteredData(nonFolderFiles);
          // console.log("updated files:", value);
        }

        if (parseInt(value) === 0) {
          setAllFiles(filteredFiles);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateDepartmentFiles = async (deptName) => {
    try {
      const response = await api.get(`/file/files/${deptName}`);
      console.log(response);

      if (response) {
        const mappedFiles = response.data.map((file) => {
          // destucture and extract dept name of every file
          try {
            const [{ depts }, ...extra] = file.file_info;
            const [{ name }, ...more] = depts;
            file.department = name;
          } catch (err) {
            // if department {depts:[]} is empty
            // console.log(err);
            file.department = "";
          }
          // console.log("department : ", file.department);
          const filteredDepartment = departments.filter(
            (dept) => dept.id === file.department_ids[0]
          );

          return {
            id: file.id,
            versionId: file.versionId ?? "",
            folderId: file.folder_id ?? "",
            name: file?.name?.substring(0, 80),
            profilePic: file.profile_pic,
            size: formatFileSize(file.metadata.size),
            dept: file.department_ids,
            owner: file.owner_email,
            mimetype: file.metadata.mimetype,
            status: "Team",
            security: "Enhanced",
            color: filteredDepartment[0]?.metadata.bg,
            isLocked: file?.is_locked,
            hasPassword: file?.has_password,
            password: file?.password,
            isPinned: file?.is_pinned,
            drive_link: file?.drive_link,
            drive_file_id: file?.drive_file_id,
            lastUpdate: new Date(file.metadata.lastModified).toLocaleString(
              "en-IN",
              {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              }
            ),
          };
        });

        // Sort the mappedFiles array based on the lastUpdate property
        mappedFiles.sort((a, b) => {
          return new Date(b.lastUpdate) - new Date(a.lastUpdate);
        });

        console.log(departments);

        const getDepartmentId = departments.find(
          (dept) => dept.name === deptName
        );

        console.log(getDepartmentId);

        const filteredFiles = mappedFiles.filter((file) =>
          file.dept.find((id) => id === getDepartmentId.id)
        );

        const usersFiles = filteredFiles.filter((file) => {
          try {
            const profileData = JSON.parse(
              secureLocalStorage.getItem("profileData")
            );
            if (
              profileData.role_priv !== "org_admin" &&
              file.owner !== profileData.id
            ) {
              return null;
            }
            // Note: pinned files (may be do not show pinned files from all departments)
            // if (
            //   value === 4 &&
            //   file.owner !== profileData.id &&
            //   profileData.role_priv !== "org_admin"
            // ) {
            //   return null;
            // }
            return file;
          } catch (err) {
            console.log(err);
            return null;
          }
        });

        // filter out files which have a folder id
        const nonFolderFiles = usersFiles;

        console.log({ usersFiles, nonFolderFiles });
        console.log(nonFolderFiles);
        setDepartmentFiles(nonFolderFiles);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  return (
    <fileContext.Provider
      value={{
        files,
        setFiles,
        filteredData,
        setFilteredData,
        updateFilesState,
        removeFile,
        anchorEl,
        setAnchorEl,
        updateDepartmentFiles,
        departmentFiles,
        setDepartmentFiles,
        selectedFiles,
        setSelectedFiles,
        folders,
        setFolders,
        isMultiSelect,
        setIsMultiSelect,
        openInChat,
        setOpenInChat,
        refreshRecycleBin,
        setRefreshRecycleBin,
        refreshFolders,
        setRefreshFolders,
        allFiles,
        currFile,
        setCurrFile,
        currFileMap,
        setCurrFileFor,
        getCurrFileFor,
      }}
    >
      {props.children}
    </fileContext.Provider>
  );
};

export default fileContext;
